import React, { useState, useEffect } from 'react';
import { Search, ChevronDown, ChevronUp, ChevronRight, Menu, X } from 'lucide-react';
import { useI18n } from '../hooks/useI18n';
import SearchModal from './SearchModal';
import AccessibilityPanel from './AccessibilityPanel';

// Complete menu structure matching CISH website
const menuData = [
  { label: 'HOME', href: '/' },
  {
    label: 'ABOUT US',
    href: '/about',
    submenu: [
      { label: 'About Institute', href: '/about' },
      { label: 'Field Gene Bank', href: '#' },
      { label: 'Divisions', href: '/division' },
      { label: 'PME', href: '#' },
      { label: 'ITMU/IPR Cells', href: '/itmu' },
      { label: 'Agri Business Incubation Center', href: '/abi' },
      { label: 'AKMU', href: '#' },
      { label: 'Regional Research Station', href: '/rrs-malda' },
      { label: 'Krishi Vigyan Kendra', href: '/kvk-malda' },
      { label: 'Cadres Strength', href: '/cadre-strength' },
      { label: 'Staff', href: '/staff' },
      { label: 'Citizen Charter', href: '/citizen-charter' },
    ]
  },
  {
    label: 'RESEARCH',
    submenu: [
      { label: 'Institute/External Projects', href: '/research-funded' },
      { label: 'Collaborations', href: '/collaboration' },
    ]
  },
  {
    label: 'PUBLICATIONS',
    submenu: [
      { label: 'Annual Reports', href: '/annual-report' },
      { label: 'E-Publications', href: '/e-publications' },
      { label: 'Newsletter (Subscription)', href: '/newsletter' },
      { label: 'Udyan Rashmi (Subscription)', href: '/udyan-rashmi' },
      { label: 'CISH Publications (Paid & Free)', href: '/cish-publications' },
      { label: 'Vision 2050', href: '/vision2050' },
    ]
  },
  { label: 'TECHNOLOGIES', href: '/technologies' },
  {
    label: 'VARIETIES',
    href: '/varieties'
  },
  {
    label: 'FOR FARMERS',
    href: '#',
    submenu: [
      { label: 'Agro Advisory', href: '/agro-advisory' },
      { label: 'CISH Farmer Portal (CFP)', href: 'https://cishfarmerportal.org', external: true },
      { label: 'Plant Bookings', href: '/plant-bookings' },
      { label: 'Success Stories (of Farmers)', href: '/success-stories' },
      { label: 'Farmer Friendly Technologies', href: '/technologies' },
    ]
  },
  { label: 'NURSERY', href: '/nursery' },
  {
    label: 'MEDIA',
    href: '/media',
    submenu: [
      { label: 'Photos', href: '/photos' },
      { label: 'Videos', href: '/videos' },
      { label: 'Events', href: '/events' },
    ]
  },
  { label: 'CONTACT US', href: '/contact' },
];

export default function Header() {
  const { language, changeLanguage, t } = useI18n();
  // Note: fontSize state moved to AccessibilityPanel component
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState(null);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileMenuOpen(false);
        setMobileSubmenuOpen(null);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load Bhashini Translation Plugin
  useEffect(() => {
    const loadBhashiniScript = () => {
      // Check if script already exists
      if (document.querySelector('script[src*="bhashini"]')) {
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://translation-plugin.bhashini.co.in/v3/website_translation_utility.js';
      script.async = true;
      script.setAttribute('language-icon-color', '#0E6D2B');
      document.body.appendChild(script);

      console.log('✅ Bhashini Translation Plugin loaded');
    };

    loadBhashiniScript();
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  // Font size adjustment moved to AccessibilityPanel component

  const handleMenuEnter = (label) => {
    setOpenMenu(label);
  };

  const handleMenuLeave = () => {
    setOpenMenu(null);
  };

  const toggleMobileSubmenu = (label) => {
    setMobileSubmenuOpen(mobileSubmenuOpen === label ? null : label);
  };

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm" data-testid="header">
      {/* Main header content */}

      {/* Top bar - Accessibility + Bhashini - INLINE LAYOUT */}
      <div className="bg-white border-b border-[#E6E6E6] relative">
        <div className="container-cish">
          <div className="flex items-center justify-between py-2">
            {/* Left side - Skip link for accessibility - Always Visible */}
            <a href="#main-content" className="skip-link-nav" data-testid="skip-link">
              {t('skipToContent')}
            </a>

            {/* Right side - Accessibility + Bhashini */}
            <div className="flex items-center gap-2">
              <AccessibilityPanel />

              {/* Separator */}
              <div className="h-5 w-px bg-[#E6E6E6] hidden sm:block"></div>

              {/* Bhashini - ALWAYS VISIBLE on all screen sizes */}
              <div className="flex items-center bhashini-wrapper">
                <div className="bhashini-plugin-container"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Main header with logo */}
        <div className="container-cish">
          <div className="flex items-center justify-between py-2 md:py-3">
            {/* Logo and Institute Name */}
            <a
              href="https://www.cish.org.in"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 md:gap-4 hover:opacity-90 transition-opacity flex-1"
              data-testid="logo-link"
            >
              {/* ICAR Logo */}
              <img
                src="/images/icar-logo.webp"
                alt="ICAR Logo"
                className="w-12 h-12 md:w-16 md:h-16 object-contain flex-shrink-0"
                data-testid="icar-logo"
              />
              <div className="min-w-0 flex-1">
                {/* Hindi name */}
                <p className="text-[10px] sm:text-xs md:text-sm text-[#6B6B6B] font-hindi leading-tight font-bold">
                  भा.कृ.अनु.प.-केन्द्रीय उपोष्ण बागवानी संस्थान
                </p>
                {/* English name - Two lines on mobile, single line on desktop */}
                <p className="text-[10px] sm:text-sm md:text-base lg:text-lg font-semibold text-[#0E6D2B] leading-tight">
                  <span className="hidden md:inline">ICAR-CENTRAL INSTITUTE FOR SUBTROPICAL HORTICULTURE</span>
                  <span className="md:hidden leading-snug">
                    ICAR-CENTRAL INSTITUTE<br />FOR SUBTROPICAL HORTICULTURE
                  </span>
                </p>
              </div>
            </a>

            {/* Right side: Search + Menu icons */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Desktop Search bar */}
              <div className="relative hidden lg:block">
                <input
                  type="search"
                  placeholder="Search..."
                  className="w-64 h-10 pl-4 pr-10 text-sm border border-[#E6E6E6] rounded focus:outline-none focus:border-[#0E6D2B] cursor-pointer"
                  aria-label="Search"
                  onClick={() => setSearchModalOpen(true)}
                  onFocus={() => setSearchModalOpen(true)}
                  readOnly
                />
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B6B] pointer-events-none" />
              </div>

              {/* Mobile: Search icon button */}
              <button
                onClick={() => setSearchModalOpen(true)}
                className="lg:hidden w-10 h-10 flex items-center justify-center text-[#4A4A4A] hover:bg-gray-100 rounded flex-shrink-0"
                aria-label="Search"
                data-testid="mobile-search"
              >
                <Search className="w-5 h-5" />
              </button>

              {/* Mobile: Hamburger menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden w-10 h-10 flex items-center justify-center text-[#4A4A4A] hover:bg-gray-100 rounded flex-shrink-0"
                aria-label="Toggle menu"
                data-testid="hamburger-menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Navigation menu - hidden on mobile */}
      <nav className="bg-white border-t border-[#E6E6E6] hidden lg:block" data-testid="main-nav">
        <div className="container-cish">
          <ul className="flex items-center gap-0">
            {menuData.map((item, index) => (
              <li
                key={index}
                className="relative"
                onMouseEnter={() => item.submenu && handleMenuEnter(item.label)}
                onMouseLeave={handleMenuLeave}
              >
                {item.href ? (
                  <a
                    href={item.href}
                    className={`flex items-center gap-1 px-4 py-3 text-[13px] font-medium transition-colors ${openMenu === item.label
                      ? 'text-[#0E6D2B] bg-[#F4F8EF]'
                      : 'text-[#4A4A4A] hover:text-[#0E6D2B] hover:bg-[#F4F8EF]'
                      }`}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.label}
                    {item.submenu && (
                      openMenu === item.label
                        ? <ChevronUp className="w-3 h-3" />
                        : <ChevronDown className="w-3 h-3" />
                    )}
                  </a>
                ) : (
                  <button
                    className={`flex items-center gap-1 px-4 py-3 text-[13px] font-medium transition-colors ${openMenu === item.label
                      ? 'text-[#0E6D2B] bg-[#F4F8EF]'
                      : 'text-[#4A4A4A] hover:text-[#0E6D2B] hover:bg-[#F4F8EF]'
                      }`}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.label}
                    {item.submenu && (
                      openMenu === item.label
                        ? <ChevronUp className="w-3 h-3" />
                        : <ChevronDown className="w-3 h-3" />
                    )}
                  </button>
                )}

                {/* Dropdown menu */}
                {item.submenu && openMenu === item.label && (
                  <div
                    className="absolute left-0 top-full bg-[#4A4A4A] shadow-lg min-w-[280px] z-50 py-2"
                    data-testid={`dropdown-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.submenu.map((subItem, subIndex) => (
                      <a
                        key={subIndex}
                        href={subItem.href}
                        className="block px-5 py-3 text-[14px] text-white hover:bg-[#5A5A5A] hover:text-[#90EE90] transition-colors"
                        data-testid={`dropdown-item-${subIndex}`}
                        {...(subItem.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      >
                        {subItem.label}
                        {subItem.external && <span className="ml-1 text-xs">↗</span>}
                      </a>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Mobile Navigation Menu - Slide-in panel */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-[60px] sm:top-[100px] z-40">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
          />

          {/* Menu panel */}
          <nav
            className="absolute top-0 left-0 w-[85%] max-w-[320px] h-full bg-white shadow-xl overflow-y-auto"
            data-testid="mobile-nav"
          >
            {/* Mobile Search */}
            <div className="p-4 border-b border-[#E6E6E6]">
              <div className="relative">
                <input
                  type="search"
                  placeholder={language === 'hi' ? 'खोजें...' : 'Search...'}
                  className="w-full h-10 pl-4 pr-10 text-sm border border-[#E6E6E6] rounded focus:outline-none focus:border-[#0E6D2B] cursor-pointer"
                  aria-label="Search"
                  onClick={() => {
                    setSearchModalOpen(true);
                    setMobileMenuOpen(false);
                  }}
                  onFocus={() => {
                    setSearchModalOpen(true);
                    setMobileMenuOpen(false);
                  }}
                  readOnly
                />
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B6B] pointer-events-none" />
              </div>
            </div>

            {/* Menu items */}
            <ul className="py-2">
              {menuData.map((item, index) => (
                <li key={index} className="border-b border-[#E6E6E6] last:border-b-0">
                  {item.submenu ? (
                    <>
                      <button
                        onClick={() => toggleMobileSubmenu(item.label)}
                        className="w-full flex items-center justify-between px-4 py-3 text-[14px] font-medium text-[#4A4A4A]"
                        data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {item.label}
                        <ChevronRight className={`w-4 h-4 transition-transform ${mobileSubmenuOpen === item.label ? 'rotate-90' : ''}`} />
                      </button>

                      {/* Mobile submenu */}
                      {mobileSubmenuOpen === item.label && (
                        <ul className="bg-[#F4F8EF] py-1">
                          {item.submenu.map((subItem, subIndex) => (
                            <li key={subIndex}>
                              <a
                                href={subItem.href}
                                className="block px-6 py-2.5 text-[13px] text-[#4A4A4A] hover:text-[#0E6D2B] hover:bg-[#E8F0E4]"
                                onClick={() => setMobileMenuOpen(false)}
                              >
                                {subItem.label}
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    <a
                      href={item.href}
                      className="block px-4 py-3 text-[14px] font-medium text-[#4A4A4A] hover:text-[#0E6D2B] hover:bg-[#F4F8EF]"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {item.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </div>
      )}

      {/* Search Modal */}
      <SearchModal
        isOpen={searchModalOpen}
        onClose={() => setSearchModalOpen(false)}
        initialQuery={searchQuery}
      />
    </header>
  );
}
